import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaYoutube, FaGlobe, FaLink } from 'react-icons/fa';
import api from '../services/api';

const Bar = styled.footer`
  background: linear-gradient(180deg, #1a1f2e 0%, #0f1419 100%);
  border-top: 3px solid ${({ theme }) => theme.colors.green[600]};
  margin-top: 60px;
  color: ${({ theme }) => theme.colors.gray[100]};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => theme.colors.green[500]}, transparent);
  }
`;

const Wrap = styled.div`
  max-width: ${({ theme }) => theme.container};
  margin: 0 auto;
  padding: 48px 16px 24px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 48px;
  
  @media (max-width: 768px) {
    gap: 32px;
    padding: 32px 16px 20px;
  }
`;

const Section = styled.div`
  position: relative;
  padding-left: 16px;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: linear-gradient(180deg, ${({ theme }) => theme.colors.green[500]}, ${({ theme }) => theme.colors.orange[500]});
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    padding-left: 12px;
    
    &::before {
      width: 2px;
    }
  }
`;

const Title = styled.h4`
  margin: 0 0 16px 0;
  color: ${({ theme }) => theme.colors.white};
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  font-size: 0.875rem;
  position: relative;
  padding-bottom: 8px;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 32px;
    height: 2px;
    background: ${({ theme }) => theme.colors.orange[500]};
    border-radius: 1px;
  }
`;

const Item = styled.div`
  color: ${({ theme }) => theme.colors.gray[300]};
  line-height: 1.8;
  font-size: 0.9375rem;
  margin-bottom: 8px;
  transition: all 0.2s ease;

  a {
    color: ${({ theme }) => theme.colors.gray[300]};
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    transition: all 0.2s ease;
    position: relative;
    
    &::before {
      content: '\203a';
      margin-right: 6px;
      opacity: 0;
      transform: translateX(-4px);
      transition: all 0.2s ease;
      color: ${({ theme }) => theme.colors.orange[400]};
    }
  }

  a:hover {
    color: ${({ theme }) => theme.colors.white};
    padding-left: 4px;
    
    &::before {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const Copyright = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 20px 16px;
  margin-top: 24px;
`;

const CopyrightText = styled.p`
  max-width: ${({ theme }) => theme.container};
  margin: 0 auto;
  text-align: center;
  color: ${({ theme }) => theme.colors.gray[400]};
  font-size: 0.875rem;
  letter-spacing: 0.3px;
`;

const SocialRow = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const SocialIconLink = styled.a`
  width: 32px;
  height: 32px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  color: ${({ theme }) => theme.colors.gray[100]};
  text-decoration: none;
  border: 1px solid rgba(255, 255, 255, 0.12);
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out, transform 0.15s ease-in-out;
  svg { font-size: 16px; }
  &:hover{
    background: ${({ theme }) => theme.colors.green[600]};
    color: #fff;
    transform: translateY(-1px);
  }
`;

const FooterLogo = styled.img`
  height: 60px; width: auto; margin-bottom: 16px; border-radius: 4px;
`;

const getPlatformIcon = (platform) => {
  const key = (platform || '').toLowerCase();
  if (key === 'facebook') return FaFacebookF;
  if (key === 'twitter' || key === 'x') return FaTwitter;
  if (key === 'instagram') return FaInstagram;
  if (key === 'linkedin') return FaLinkedinIn;
  if (key === 'youtube') return FaYoutube;
  if (key === 'website') return FaGlobe;
  return FaLink;
};

const getPlatformLabel = (platform, fallback) => {
  const key = (platform || '').toLowerCase();
  if (key === 'facebook') return 'Facebook';
  if (key === 'twitter' || key === 'x') return 'Twitter';
  if (key === 'instagram') return 'Instagram';
  if (key === 'linkedin') return 'LinkedIn';
  if (key === 'youtube') return 'YouTube';
  if (key === 'website') return 'Website';
  return fallback || 'Social link';
};

const Footer = () => {
  const [info, setInfo] = useState({ address: '', email: '', phone: '', hotlinePhone: '', hoursOfOperation: '' });
  const [socialLinks, setSocialLinks] = useState([]);

  useEffect(() => {
    let mounted = true;
    api.home.get().then(data => {
      if (mounted && data?.footer) {
        setInfo(prev => ({
          ...prev,
          address: data.footer.address || '',
          email: data.footer.email || '',
          phone: data.footer.phone || ''
        }));
      }
    }).catch(() => {});
    api.contact.getPage().then(data => {
      if (mounted && data) {
        setInfo(prev => ({
          ...prev,
          hotlinePhone: data.hotlinePhone || '',
          hoursOfOperation: data.hoursOfOperation || ''
        }));
        if (Array.isArray(data.socialLinks)) {
          setSocialLinks(data.socialLinks);
        }
      }
    }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  return (
    <Bar>
      <Wrap>
        <Section>
          <FooterLogo src="/assests/logo/logo.jpeg" alt="FAL Logo" />
          <Title>Food Authority Of Liberia</Title>
          <Item>Ensuring safety and quality across the food supply chain to protect consumers and promote fair trade practices.</Item>
        </Section>
        <Section>
          <Title>Quick Links</Title>
          <Item><Link to="/">Home</Link></Item>
          <Item><Link to="/about">About Us</Link></Item>
          <Item><Link to="/objectives">Objectives</Link></Item>
          <Item><Link to="/news">News & Media</Link></Item>
          <Item><Link to="/team">Our Team</Link></Item>
          <Item><Link to="/gallery">Gallery</Link></Item>
          <Item><Link to="/partnerships">Partnerships</Link></Item>
          <Item><Link to="/contact">Contact Us</Link></Item>
          <Item><Link to="/admin/login">Admin Login</Link></Item>
        </Section>
        <Section>
          <Title>Contact Info</Title>
          <Item><strong>Address:</strong><br/>{info.address || 'Monrovia, Liberia'}</Item>
          <Item><strong>Email:</strong><br/>{info.email ? <a href={`mailto:${info.email}`}>{info.email}</a> : 'info@fal.gov.lr'}</Item>
          <Item><strong>Phone:</strong><br/>{info.phone || '+231 000 000 000'}</Item>
          {info.hotlinePhone ? <Item><strong>Hotline:</strong><br/>{info.hotlinePhone}</Item> : null}
          {info.hoursOfOperation ? <Item><strong>Hours of Operation:</strong><br/><span style={{ whiteSpace: 'pre-wrap' }}>{info.hoursOfOperation}</span></Item> : null}
          {socialLinks.length > 0 && (
            <SocialRow>
              {socialLinks.map((link, idx) => {
                if (!link?.url) return null;
                const Icon = getPlatformIcon(link.platform);
                const label = getPlatformLabel(link.platform, link.label);
                return (
                  <SocialIconLink
                    key={idx}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    title={label}
                  >
                    <Icon />
                  </SocialIconLink>
                );
              })}
            </SocialRow>
          )}
        </Section>
      </Wrap>
      <Copyright>
        <CopyrightText>
          © {new Date().getFullYear()} Food Authority Of Liberia. All rights reserved.
        </CopyrightText>
      </Copyright>
    </Bar>
  );
};

export default Footer;
